import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { Company, Dictionary } from '~/types/general';
import { IJCState } from '~/types/models/jcState';
import { IJCStatus } from '~/types/models/jcStatus';
import { IReportType } from '~/types/models/reportType';
import { JCStatesAdapter, JCStatusesAdapter, ReportTypesAdapter } from './adapters';
import { extraReducers } from './extraReducers';
import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import { AlertOptions } from 'react-notification-alert';
import { IDepartmentResponse } from '~/types/response/department';
import { ISubDepartmentResponse } from '~/types/response/subDepartment';
import { IWorkstationResponse } from '~/types/response/workstation';

export type NotificationT = Partial<AlertOptions>;
export interface MainState {
  dictionaries: {
    jcStates: EntityState<IJCState> & { loading: boolean };
    jcStatuses: EntityState<IJCStatus> & { loading: boolean };
    reportTypes: EntityState<IReportType> & { loading: boolean };
    branches: Dictionary[];
    companies: Company[];
    departments: IDepartmentResponse[];
    subDepartments: ISubDepartmentResponse[];
    workstations: IWorkstationResponse[];
  };
  loaders: {
    jcStatuses: boolean;
    jcStates: boolean;
    branches: boolean;
    companies: boolean;
    departments: boolean;
    subDepartments: boolean;
    workstations: boolean;
  };
  notification: NotificationT;
  logo: string;
}

const initialState: MainState = {
  dictionaries: {
    jcStates: JCStatesAdapter.getInitialState({ loading: true }),
    jcStatuses: JCStatusesAdapter.getInitialState({ loading: true }),
    reportTypes: ReportTypesAdapter.getInitialState({ loading: true }),
    branches: [],
    companies: [],
    departments: [],
    subDepartments: [],
    workstations: [],
  },
  loaders: {
    jcStatuses: true,
    jcStates: true,
    branches: true,
    companies: true,
    departments: true,
    subDepartments: true,
    workstations: true,
  },
  notification: {},
  logo: '',
};

export const { actions, reducer } = createSlice({
  name: 'main',
  initialState,
  reducers: {
    setNotification: (state: MainState, action: PayloadAction<NotificationT>) => {
      state.notification = action.payload;
    },
    clearNotification: (state: MainState) => {
      state.notification = {};
    },
    clearAllDictionaries: (state: MainState) => {
      state.dictionaries = {
        jcStates: JCStatesAdapter.getInitialState({ loading: true }),
        jcStatuses: JCStatusesAdapter.getInitialState({ loading: true }),
        reportTypes: ReportTypesAdapter.getInitialState({ loading: true }),
        branches: [],
        companies: [],
        departments: [],
        subDepartments: [],
        workstations: [],
      };
    },
  },
  extraReducers,
});

export const { setNotification, clearNotification, clearAllDictionaries } = actions;

const persistConfig = {
  key: 'main',
  version: 1,
  storage,
  blacklist: ['logo'],
};

export const persistReducerMain = persistReducer(persistConfig, reducer);
